import {message} from "antd";
import {AxiosError, AxiosRequestConfig} from "axios";
import {API_BASE_URL} from "../services/config";
import axiosInstance from "../mixpanel/axiosInstance";

export interface ICustomConfig extends AxiosRequestConfig {
  token?: string;
  data?: unknown;
}

// type TClientArgs = (url: string) => Promise<AxiosResponse<any, any>>;

/**
 * Endpoints that establish a session and therefore must never carry one.
 *
 * `useClient` injects the persisted `userLogin.accessToken` into every request
 * it makes, and that state outlives the token itself (it is redux-persisted to
 * localStorage). Sending it here makes the backend reject the request with
 * "Unauthorized. Invalid or expired token" before it ever looks at the body,
 * so a customer whose token has expired can no longer log in or activate —
 * the one thing that would have given them a fresh token. A live coach token
 * fails the same way: right token, wrong audience.
 *
 * The rule lives here rather than at the call site because `client` is the
 * single place the header is attached; leaving it to callers means every
 * future caller of these endpoints has to remember.
 */
const UNAUTHENTICATED_ENDPOINTS = ["customers/auth/login", "customers/auth/confirm"];

const isUnauthenticatedEndpoint = (url: string) => {
  const normalized = url.split("?")[0].replace(/^\/+/, "");
  return UNAUTHENTICATED_ENDPOINTS.includes(normalized);
};

export const client = async (
  url: string,
  {token, headers: customHeaders, ...customConfig}: ICustomConfig,
  baseUrl?: string
) => {
  const authToken = isUnauthenticatedEndpoint(url) ? undefined : token;

  const config: AxiosRequestConfig = {
    baseURL: baseUrl ?? API_BASE_URL,
    url,
    headers: {
      ...(authToken && {Authorization: `Bearer ${authToken}`}),
      ...customHeaders,
    },
    ...customConfig,
  };
  // console.log({axiosInstance: axiosInstance.request(config)});
  return axiosInstance
    .request(config)
    .then(async (response) => {
      // console.log({response: response.status});
      const data = response.data;
      return data;
    })
    .catch((err: AxiosError<{status: boolean; message: string}>) => {
      if (err.response?.status === 413) {
        message.error(err.response.statusText, 1);
        message.error("Check image(s) size(s)", 2);
      }
      if (err.response?.status === 401) {
        // console.log({errAx: err});
        message.warn(err.response.data.message);
        // logoutReset();
        // // // refresh the page for them
        // window.location.assign(`${window.location.origin}/auth/login`);
      }
      return Promise.reject(err.response?.data);
    });
};
